import { Fragment, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { api, type DocTreeNode } from '../api';
import { Markdown } from '../ui';

/**
 * Wiki standalone do projeto: a base de conhecimento (docs/) renderizada como um
 * site de documentação próprio — FORA do layout/nav do bug-pilot. Somente leitura,
 * navegável e com URL profunda (?path=docs/...). Páginas .html abrem em iframe
 * sandboxed (e podem abrir sozinhas em nova aba via doc-view).
 */
export function Wiki() {
  const { slug } = useParams<{ slug: string }>();
  const [params, setParams] = useSearchParams();
  const path = params.get('path');

  const [projectName, setProjectName] = useState('');
  const [tree, setTree] = useState<DocTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [loadingFile, setLoadingFile] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['docs']));
  const [navOpen, setNavOpen] = useState(true);

  useEffect(() => {
    if (!slug) return;
    document.title = `Wiki — ${slug}`;
    Promise.all([
      api.getProject(slug).then((r) => setProjectName(r.project.name)).catch(() => undefined),
      api.getDocsTree(slug).then((r) => setTree(r.tree)).catch(() => undefined),
    ]).finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!slug || !path) {
      setContent('');
      return;
    }
    setLoadingFile(true);
    api
      .getDoc(slug, path)
      .then((r) => setContent(r.content))
      .catch(() => setContent('*(não foi possível carregar este documento)*'))
      .finally(() => setLoadingFile(false));
    // expande as pastas do caminho ao abrir via URL
    setExpanded((s) => {
      const n = new Set(s);
      const segs = path.split('/');
      for (let i = 1; i < segs.length; i++) n.add(segs.slice(0, i).join('/'));
      return n;
    });
  }, [slug, path]);

  function open(p: string) {
    setParams({ path: p });
    if (window.matchMedia('(max-width: 760px)').matches) setNavOpen(false);
  }

  const isHtml = path ? /\.html?$/i.test(path) : false;
  const firstDoc = useMemo(() => findFirstDoc(tree), [tree]);

  if (loading) return <div className="wiki-center">Carregando…</div>;

  return (
    <div className="wiki">
      <header className="wiki-top">
        <button className="wiki-burger" onClick={() => setNavOpen((o) => !o)} title="Menu" aria-label="Menu">☰</button>
        <div className="wiki-brand">
          <span className="wiki-brand-name">{projectName || slug}</span>
          <span className="wiki-brand-sub">wiki do projeto</span>
        </div>
        <div className="wiki-top-right">
          {isHtml && slug && path && (
            <a className="wiki-tab-link" href={api.docViewUrl(slug, path)} target="_blank" rel="noopener noreferrer">abrir página sozinha ↗</a>
          )}
          <Link className="wiki-tab-link" to={`/p/${slug}/knowledge`}>editar no Neo-Pilots →</Link>
        </div>
      </header>

      <div className="wiki-body">
        <aside className={`wiki-nav ${navOpen ? 'is-open' : ''}`}>
          {tree.length === 0 ? (
            <p className="wiki-nav-empty">Nenhum documento ainda.</p>
          ) : (
            <WikiTree nodes={tree} depth={0} expanded={expanded} current={path} onToggle={(p) => setExpanded((s) => { const n = new Set(s); n.has(p) ? n.delete(p) : n.add(p); return n; })} onOpen={open} />
          )}
        </aside>

        <main className="wiki-main">
          {!path ? (
            <div className="wiki-welcome">
              <h1>{projectName || slug}</h1>
              <p>Bem-vindo à wiki do projeto. Escolha um documento no menu à esquerda.</p>
              {firstDoc && (
                <button className="wiki-cta" onClick={() => open(firstDoc)}>Começar a ler →</button>
              )}
            </div>
          ) : loadingFile ? (
            <div className="wiki-center">Carregando documento…</div>
          ) : isHtml ? (
            <iframe className="wiki-html-frame" sandbox="" srcDoc={content} title={path} />
          ) : (
            <article className="wiki-article">
              <Markdown text={content} />
            </article>
          )}
        </main>
      </div>
    </div>
  );
}

function WikiTree({ nodes, depth, expanded, current, onToggle, onOpen }: {
  nodes: DocTreeNode[]; depth: number; expanded: Set<string>; current: string | null;
  onToggle: (p: string) => void; onOpen: (p: string) => void;
}) {
  return (
    <>
      {nodes.map((n) => {
        const pad = { paddingLeft: 8 + depth * 14 } as const;
        if (n.type === 'dir') {
          const open = expanded.has(n.path);
          return (
            <Fragment key={n.path}>
              <button type="button" className="wiki-nav-dir" style={pad} onClick={() => onToggle(n.path)}>
                {open ? '▾' : '▸'} {n.name}
              </button>
              {open && n.children && (
                <WikiTree nodes={n.children} depth={depth + 1} expanded={expanded} current={current} onToggle={onToggle} onOpen={onOpen} />
              )}
            </Fragment>
          );
        }
        const label = n.name.replace(/\.(md|markdown|txt|html?|htm)$/i, '');
        return (
          <button type="button" key={n.path} className={`wiki-nav-file ${current === n.path ? 'is-active' : ''}`} style={pad} onClick={() => onOpen(n.path)}>
            {n.name === 'index.md' ? '🗂️' : /\.html?$/i.test(n.name) ? '🌐' : '📄'} {label}
          </button>
        );
      })}
    </>
  );
}

function findFirstDoc(nodes: DocTreeNode[]): string | null {
  for (const n of nodes) {
    if (n.type === 'file') return n.path;
    if (n.children) {
      const f = findFirstDoc(n.children);
      if (f) return f;
    }
  }
  return null;
}
